import React from 'react';
import { Box, Typography, Button, Stack, InputAdornment } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import { useForm, FormProvider } from 'react-hook-form';
import { FormTextField } from '@personal-dashboard/ui';

interface RecipeToolbarProps {
  onAdd: () => void;
  onSearchChange: (query: string) => void;
}

interface SearchForm {
  search: string;
}

export const RecipeToolbar: React.FC<RecipeToolbarProps> = ({ onAdd, onSearchChange }) => {
  const searchMethods = useForm<SearchForm>({ defaultValues: { search: '' } });
  const searchValue = searchMethods.watch('search');
  const lastEmitted = React.useRef('');

  React.useEffect(() => {
    const val = searchValue || '';
    if (val !== lastEmitted.current) {
      lastEmitted.current = val;
      onSearchChange(val);
    }
  }, [searchValue, onSearchChange]);

  return (
    <Box sx={{ mb: 4 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={2}>
        <Box>
          <Typography variant="h4" color="primary" sx={{ fontWeight: 'bold' }}>
            Recipe Tracker
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Manage your recipes and meals
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={onAdd}
          sx={{ alignSelf: { xs: 'stretch', sm: 'auto' } }}
        >
          Add Recipe
        </Button>
      </Stack>

      <Box sx={{ mt: 2 }}>
        <FormProvider {...searchMethods}>
          <FormTextField
            name="search"
            fullWidth
            variant="outlined"
            placeholder="Search recipes by title, ingredients, or instructions..."
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
        </FormProvider>
      </Box>
    </Box>
  );
};

export default RecipeToolbar;
